(() => {
  const configElement = document.getElementById('nuo-explicit-variant-config');

  if (!configElement) return;

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('NUO explicit variant selection: invalid configuration.', error);
    return;
  }

  if (!config.enabled) return;

  const states = new Map();
  const userChoiceInputs = new WeakSet();
  const kachingConfirmedSelectors = new Set();
  const kachingPendingChoices = new Map();
  const kachingStatuses = new WeakMap();
  const kachingStatusTimers = new WeakMap();
  let activeDialog = null;
  let dialogErrorTimer = null;
  let toastTimer = null;
  let kachingRefreshTimer = null;
  let lastAddedSelection = null;

  const escapeSelector = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/([ #;?%&,.+*~\\':"!^$[\]()=>|/@])/g, '\\$1');
  };

  const mergeText = (template, values) => Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`[${key}]`, value || ''),
    template || ''
  );

  const naturalizeHeading = (value) => String(value || '').split(/(\s+)/).map((token) => {
    const letters = token.match(/\p{L}/gu) || [];
    const isAllCaps = letters.length > 0
      && token === token.toLocaleUpperCase('ro-RO')
      && token !== token.toLocaleLowerCase('ro-RO');

    if (!isAllCaps || letters.length <= 3 || /\d/.test(token)) return token;
    return `${token.charAt(0).toLocaleUpperCase('ro-RO')}${token.slice(1).toLocaleLowerCase('ro-RO')}`;
  }).join('');

  const pickerKey = (picker) => picker.getAttribute('form-id');

  const requiredFields = (picker) => Array.from(
    picker.querySelectorAll('.variant-picker__option[data-nuo-explicit-choice]')
  );

  const choiceText = (picker, singular, plural) => (
    requiredFields(picker).length > 1 ? (plural || singular) : singular
  );

  const optionPosition = (field) => field.dataset.optionPosition || field.querySelector('[data-option-position]')?.dataset.optionPosition;

  const getState = (picker) => {
    const key = pickerKey(picker);

    if (!states.has(key)) {
      states.set(key, {
        confirmed: new Set(),
        bypassOnce: false,
        attempted: false,
        variantUpdating: false,
        popupCommitted: false,
        popupSubmitPending: false,
        sourceModalId: picker.closest('quick-buy-modal')?.id || null
      });
    }

    const state = states.get(key);
    const quickBuyModal = picker.closest('quick-buy-modal');

    if (quickBuyModal?.dataset.nuoExplicitCardVariant) {
      const cardOptionPosition = quickBuyModal.dataset.nuoExplicitCardOptionPosition;
      requiredFields(picker)
        .filter((field) => !cardOptionPosition || optionPosition(field) === cardOptionPosition)
        .forEach((field) => state.confirmed.add(optionPosition(field)));
      state.sourceModalId = quickBuyModal.id;
    }

    return state;
  };

  const inputLabel = (input) => {
    const label = document.querySelector(`label[for="${escapeSelector(input.id)}"]`);
    return label?.querySelector('.sr-only')?.textContent.trim() || label?.textContent.trim() || input.value;
  };

  const selectedLabels = (picker, state) => requiredFields(picker)
    .filter((field) => state.confirmed.has(optionPosition(field)))
    .map((field) => {
      const checked = field.querySelector('input[data-option-position]:checked');
      return checked ? inputLabel(checked) : '';
    })
    .filter(Boolean);

  const isComplete = (picker, state) => {
    const fields = requiredFields(picker);
    return fields.length > 0 && fields.every((field) => state.confirmed.has(optionPosition(field)));
  };

  const normalizedOptionNames = (Array.isArray(config.optionNames)
    ? config.optionNames
    : String(config.optionNames || '').split(','))
    .map((name) => String(name).trim().toLocaleLowerCase('ro-RO'))
    .filter(Boolean);

  const isRelevantKachingSelector = (selector) => normalizedOptionNames.includes(
    String(selector.getAttribute('aria-label') || '').trim().toLocaleLowerCase('ro-RO')
  );

  const isVisible = (element) => Boolean(element?.isConnected && element.getClientRects().length);

  const productScope = (element) => element.closest('product-rerender')?.id
    || element.closest('quick-buy-modal')?.id
    || window.location.pathname;

  const kachingContext = (root) => {
    const selectedBar = root.querySelector('.kaching-bundles__bar--selected');
    const bars = Array.from(root.querySelectorAll('.kaching-bundles__bar'));
    const scope = productScope(root);
    const selectedBarIndex = bars.indexOf(selectedBar);
    const selectors = selectedBar
      ? Array.from(selectedBar.querySelectorAll('.kaching-bundles__bar-variant-select[role="combobox"]'))
        .filter((selector) => isRelevantKachingSelector(selector) && isVisible(selector))
      : [];
    const keyForIndex = (index) => `${scope}:${selectedBarIndex}:${index}`;

    return { selectedBar, selectors, keyForIndex, scope };
  };

  const activeKachingContexts = (picker = null) => {
    const scope = picker ? productScope(picker) : null;

    return Array.from(document.querySelectorAll('.kaching-bundles'))
      .filter(isVisible)
      .map((root) => ({ root, ...kachingContext(root) }))
      .filter((context) => (
        context.selectedBar
        && context.selectors.length > 0
        && (!scope || context.scope === scope)
      ));
  };

  const kachingIsComplete = (picker) => activeKachingContexts(picker).every(({ selectors, keyForIndex }) => (
    selectors.every((selector, index) => (
      selector.dataset.nuoChoiceConfirmed === 'true' || kachingConfirmedSelectors.has(keyForIndex(index))
    ))
  ));

  const kachingLabel = (selector) => Array.from(selector.children)
    .find((child) => child.tagName === 'SPAN');

  const selectedKachingLabels = (picker) => activeKachingContexts(picker).flatMap(({ selectors, keyForIndex }) => (
    selectors
      .filter((selector, index) => kachingConfirmedSelectors.has(keyForIndex(index)))
      .map((selector) => kachingLabel(selector)?.textContent.trim() || selector.dataset.nuoActualLabel || '')
      .filter(Boolean)
  ));

  const confirmedButtonContent = (labels) => labels.length > 1
    ? {
        primary: mergeText(config.multipleConfirmLabel, { count: String(labels.length) }),
        secondary: labels.join(' · '),
        multiple: true
      }
    : {
        primary: config.confirmLabel || config.multipleConfirmLabel,
        secondary: labels[0] || '',
        multiple: false
      };

  const kachingStatus = (root, create = false) => {
    let status = kachingStatuses.get(root);
    if (!status?.isConnected) {
      status = root.querySelector('.nuo-kaching-choice-status');
      kachingStatuses.set(root, status);
    }
    if (status || !create) return status;

    status = document.createElement('p');
    status.className = 'nuo-kaching-choice-status';
    status.setAttribute('role', 'alert');
    status.hidden = true;
    if (config.alertBackground) {
      status.style.setProperty('--nuo-variant-alert-background', config.alertBackground);
    }
    if (config.alertTextColor) {
      status.style.setProperty('--nuo-variant-alert-text', config.alertTextColor);
    }

    kachingStatuses.set(root, status);
    return status;
  };

  const positionKachingStatus = (root, status, selectedBar) => {
    if (!selectedBar?.parentNode || selectedBar.closest('.kaching-bundles') !== root) return;
    selectedBar.before(status);
  };

  const hideKachingStatus = (root) => {
    const status = kachingStatus(root);
    if (!status) return;

    window.clearTimeout(kachingStatusTimers.get(status));
    kachingStatusTimers.delete(status);
    status.hidden = true;
  };

  const showKachingStatus = (picker) => {
    activeKachingContexts(picker).forEach(({ root, selectedBar, selectors, keyForIndex }) => {
      const complete = selectors.every((selector, index) => (
        selector.dataset.nuoChoiceConfirmed === 'true' || kachingConfirmedSelectors.has(keyForIndex(index))
      ));
      if (complete) {
        hideKachingStatus(root);
        return;
      }

      const status = kachingStatus(root, true);
      positionKachingStatus(root, status, selectedBar);
      status.textContent = selectors.length > 1
        ? (config.errorLabelPlural || config.errorLabel)
        : config.errorLabel;
      status.hidden = false;
      window.clearTimeout(kachingStatusTimers.get(status));
      kachingStatusTimers.set(status, window.setTimeout(() => {
        status.hidden = true;
        kachingStatusTimers.delete(status);
      }, 4500));
    });
  };

  const updateKachingSelectors = () => {
    activeKachingContexts().forEach(({ root, selectedBar, selectors, keyForIndex }) => {
      selectors.forEach((selector, index) => {
        const key = keyForIndex(index);
        const pendingValue = kachingPendingChoices.get(key);
        const actualValue = selector.closest('.kaching-bundles-option')?.dataset.value || '';

        if (pendingValue && actualValue === pendingValue) {
          kachingPendingChoices.delete(key);
          kachingConfirmedSelectors.add(key);
          selector.dataset.nuoChoiceConfirmed = 'true';
          selector.dataset.nuoActualLabel = actualValue;
        }

        const confirmed = selector.dataset.nuoChoiceConfirmed === 'true'
          || kachingConfirmedSelectors.has(key);
        const label = kachingLabel(selector);

        selector.toggleAttribute('data-nuo-choice-pending', !confirmed);

        if (!label) return;

        if (!selector.dataset.nuoActualLabel && label.textContent.trim() !== config.promptLabel) {
          selector.dataset.nuoActualLabel = label.textContent.trim();
        }

        if (confirmed) {
          if (label.textContent.trim() === config.promptLabel && selector.dataset.nuoActualLabel) {
            label.textContent = selector.dataset.nuoActualLabel;
          }
        } else if (label.textContent.trim() !== config.promptLabel) {
          label.textContent = config.promptLabel;
        }
      });

      const complete = selectors.every((selector, index) => (
        selector.dataset.nuoChoiceConfirmed === 'true' || kachingConfirmedSelectors.has(keyForIndex(index))
      ));
      if (complete) hideKachingStatus(root);
      else {
        const status = kachingStatus(root);
        if (status && !status.hidden) positionKachingStatus(root, status, selectedBar);
      }
    });
  };

  const scheduleKachingRefresh = () => {
    if (kachingRefreshTimer !== null) return;
    kachingRefreshTimer = window.setTimeout(() => {
      kachingRefreshTimer = null;
      updateKachingSelectors();
      document.querySelectorAll('variant-picker[data-nuo-explicit-initialized="true"]').forEach(updatePicker);
    }, 40);
  };

  const pendingKachingSelector = (picker) => {
    for (const { selectors, keyForIndex } of activeKachingContexts(picker)) {
      const pendingIndex = selectors.findIndex((selector, index) => (
        selector.dataset.nuoChoiceConfirmed !== 'true' && !kachingConfirmedSelectors.has(keyForIndex(index))
      ));
      if (pendingIndex >= 0) return selectors[pendingIndex];
    }

    return null;
  };

  const focusPendingKachingChoice = (picker) => {
    const selector = pendingKachingSelector(picker);
    if (!selector) return false;

    selector.classList.remove('nuo-explicit-choice--attention');
    void selector.offsetWidth;
    selector.classList.add('nuo-explicit-choice--attention');
    selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      selector.focus({ preventScroll: true });
      if (selector.getAttribute('aria-expanded') !== 'true') selector.click();
    }, 260);
    return true;
  };

  const confirmKachingOption = (option) => {
    const listbox = option.closest('[role="listbox"]');
    if (!listbox?.id) return;

    const selector = document.querySelector(`[aria-controls="${escapeSelector(listbox.id)}"]`);
    const root = selector?.closest('.kaching-bundles');
    if (!selector || !root) return;

    const { selectors, keyForIndex } = kachingContext(root);
    const index = selectors.indexOf(selector);
    if (index < 0) return;

    const picker = root.closest('product-rerender')?.querySelector('variant-picker');
    const selectedValue = option.dataset.value || option.textContent.trim();
    const key = keyForIndex(index);
    kachingConfirmedSelectors.delete(key);
    selector.removeAttribute('data-nuo-choice-confirmed');
    kachingPendingChoices.set(key, selectedValue);
    const field = picker && requiredFields(picker).find((candidate) => Array.from(
      candidate.querySelectorAll('input[data-option-position]')
    ).some((input) => inputLabel(input) === selectedValue));
    const input = field && Array.from(field.querySelectorAll('input[data-option-position]'))
      .find((candidate) => inputLabel(candidate) === selectedValue);

    if (picker && field && input) markChoice(picker, field);
    updateKachingSelectors();
    if (picker) updatePicker(picker);
    window.setTimeout(scheduleKachingRefresh, 0);
  };

  const formButtons = (formId) => {
    const form = document.getElementById(formId);
    const externalButtons = Array.from(document.querySelectorAll(`button[type="submit"][form="${escapeSelector(formId)}"]`));
    return [...Array.from(form?.querySelectorAll('button[type="submit"]') || []), ...externalButtons]
      .filter((button, index, buttons) => buttons.indexOf(button) === index && !button.closest('.nuo-variant-choice-dialog'));
  };

  const originalButtonText = (button) => {
    const textElement = button.querySelector('.button-text') || button;

    if (!button.dataset.nuoOriginalButtonText) {
      button.dataset.nuoOriginalButtonText = textElement.textContent.trim();
    }

    return button.dataset.nuoOriginalButtonText;
  };

  const setButtonText = (button, primary, secondary = '', multiple = false) => {
    const textElement = button.querySelector('.button-text') || button;
    originalButtonText(button);

    textElement.classList.toggle('nuo-button-label--secondary', Boolean(secondary));
    textElement.classList.toggle('nuo-button-label--multiple', Boolean(secondary) && multiple);

    if (secondary) {
      const primaryElement = document.createElement('span');
      primaryElement.className = 'nuo-button-label__primary';
      primaryElement.textContent = primary;

      const secondaryElement = document.createElement('span');
      secondaryElement.className = 'nuo-button-label__secondary';
      secondaryElement.textContent = secondary;

      textElement.classList.remove('truncate-text');
      textElement.replaceChildren(primaryElement, secondaryElement);
      button.setAttribute('aria-label', `${primary}. ${secondary}`);
    } else {
      textElement.classList.add('truncate-text');
      textElement.textContent = primary;
      button.setAttribute('aria-label', primary);
    }
  };

  const restoreButtonText = (button) => {
    if (!button.dataset.nuoOriginalButtonText) return;
    const textElement = button.querySelector('.button-text') || button;
    textElement.textContent = button.dataset.nuoOriginalButtonText;
    textElement.classList.remove('truncate-text');
    textElement.classList.remove('nuo-button-label--secondary');
    textElement.classList.remove('nuo-button-label--multiple');
    button.removeAttribute('aria-label');
  };

  const updatePicker = (picker) => {
    if (!picker.isConnected) return;

    const state = getState(picker);
    const complete = isComplete(picker, state) && kachingIsComplete(picker);
    const nativeLabels = selectedLabels(picker, state);
    const kachingLabels = selectedKachingLabels(picker);
    const labels = kachingLabels.length > 0 ? kachingLabels : nativeLabels;
    const fields = requiredFields(picker);

    picker.toggleAttribute('data-nuo-choice-pending', !complete);

    fields.forEach((field) => {
      const position = optionPosition(field);
      const confirmed = state.confirmed.has(position);
      field.classList.toggle('nuo-explicit-choice--pending', !confirmed);

      const selectedValue = field.querySelector('[data-nuo-selected-option-value]');
      if (selectedValue) {
        if (!selectedValue.dataset.nuoActualValue) {
          selectedValue.dataset.nuoActualValue = selectedValue.textContent.trim();
        }

        selectedValue.textContent = confirmed
          ? (field.querySelector('input[data-option-position]:checked') ? inputLabel(field.querySelector('input[data-option-position]:checked')) : selectedValue.dataset.nuoActualValue)
          : config.promptLabel;
      }
    });

    formButtons(pickerKey(picker)).forEach((button) => {
      if (button.disabled) {
        restoreButtonText(button);
        return;
      }

      if (state.variantUpdating) {
        setButtonText(button, originalButtonText(button));
      } else if (complete) {
        const content = confirmedButtonContent(labels);
        setButtonText(button, content.primary, content.secondary, content.multiple);
      } else {
        setButtonText(button, originalButtonText(button));
      }
    });

    const form = document.getElementById(pickerKey(picker));
    form?.querySelectorAll('.shopify-payment-button').forEach((paymentButton) => {
      if (!complete) {
        paymentButton.dataset.nuoChoiceHidden = 'true';
        paymentButton.hidden = true;
      } else if (paymentButton.dataset.nuoChoiceHidden === 'true') {
        paymentButton.hidden = false;
        delete paymentButton.dataset.nuoChoiceHidden;
      }
    });

    const quickBuy = picker.closest('quick-buy-modal');
    const statusHost = quickBuy?.querySelector(
      ".product-info__block-item[data-block-type='buy-buttons']"
    ) || picker;
    let status = (quickBuy || picker).querySelector('.nuo-explicit-choice-status');

    if (!status) {
      status = document.createElement('p');
      status.className = 'nuo-explicit-choice-status';
      status.setAttribute('role', 'alert');
      if (config.alertBackground) {
        status.style.setProperty('--nuo-variant-alert-background', config.alertBackground);
      }
      if (config.alertTextColor) {
        status.style.setProperty('--nuo-variant-alert-text', config.alertTextColor);
      }
      status.hidden = true;
      statusHost.prepend(status);
    } else if (status.parentElement !== statusHost) {
      statusHost.prepend(status);
    }

    const quickBuyTitle = quickBuy?.querySelector('[slot="header"]');
    if (quickBuyTitle) {
      quickBuyTitle.textContent = choiceText(picker, config.dialogTitle, config.dialogTitlePlural);
    }

    status.textContent = choiceText(picker, config.errorLabel, config.errorLabelPlural);
    const hasActiveChoiceDialog = activeDialog?.isConnected
      && activeDialog.dataset.nuoFormId === pickerKey(picker);
    const showStatus = !complete && state.attempted && !hasActiveChoiceDialog;
    status.hidden = !showStatus;

    if (showStatus && !state.statusTimer) {
      state.statusTimer = window.setTimeout(() => {
        state.statusTimer = null;
        if (!picker.isConnected) return;
        state.attempted = false;
        updatePicker(picker);
      }, 4500);
    } else if (!showStatus && state.statusTimer) {
      window.clearTimeout(state.statusTimer);
      state.statusTimer = null;
    }
  };

  const resetKachingChoices = (picker) => {
    const scopePrefix = `${productScope(picker)}:`;

    Array.from(kachingConfirmedSelectors).forEach((key) => {
      if (key.startsWith(scopePrefix)) kachingConfirmedSelectors.delete(key);
    });
    Array.from(kachingPendingChoices.keys()).forEach((key) => {
      if (key.startsWith(scopePrefix)) kachingPendingChoices.delete(key);
    });

    const popup = picker.closest('quick-buy-modal, .nuo-variant-choice-dialog');
    (popup || picker.closest('product-rerender') || picker).querySelectorAll(
      '.kaching-bundles__bar-variant-select[role="combobox"]'
    ).forEach((selector) => {
      selector.removeAttribute('data-nuo-choice-confirmed');
      selector.toggleAttribute('data-nuo-choice-pending', true);
      const label = kachingLabel(selector);
      if (label) label.textContent = config.promptLabel;
    });
    (popup || picker.closest('product-rerender') || picker).querySelectorAll('.kaching-bundles').forEach(hideKachingStatus);
  };

  const resetPopupChoices = (picker) => {
    if (!picker?.isConnected) return;

    const state = getState(picker);
    window.clearTimeout(state.statusTimer);
    state.statusTimer = null;
    state.confirmed.clear();
    state.bypassOnce = false;
    state.attempted = false;
    state.variantUpdating = false;
    resetKachingChoices(picker);
    updatePicker(picker);
  };

  const currentPicker = (formId) => document.querySelector(`variant-picker[form-id="${escapeSelector(formId)}"]`);

  const attachVariantChangeListener = (picker) => {
    const formId = pickerKey(picker);
    const form = document.getElementById(formId);
    if (!form || form.dataset.nuoExplicitVariantListener === 'true') return;

    form.dataset.nuoExplicitVariantListener = 'true';
    const finishVariantUpdate = () => {
      const state = states.get(formId);
      if (state) state.variantUpdating = false;

      window.setTimeout(() => {
        const livePicker = currentPicker(formId);
        if (livePicker) updatePicker(livePicker);
      }, 0);
    };

    form.addEventListener('product:rerender', finishVariantUpdate);
    form.addEventListener('variant:change', finishVariantUpdate);
  };

  const markChoice = (picker, field, input = null, forceUpdating = false) => {
    const state = getState(picker);
    state.confirmed.add(optionPosition(field));
    state.attempted = false;
    state.variantUpdating = state.variantUpdating || forceUpdating || (input ? !input.checked : false);
    updatePicker(picker);
  };

  const focusPendingChoice = (picker) => {
    const state = getState(picker);
    state.attempted = true;
    updatePicker(picker);

    const field = requiredFields(picker).find((candidate) => !state.confirmed.has(optionPosition(candidate)));
    if (!field) return;

    field.classList.remove('nuo-explicit-choice--attention');
    void field.offsetWidth;
    field.classList.add('nuo-explicit-choice--attention');
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => field.querySelector('label, button, input')?.focus({ preventScroll: true }), 260);
  };

  const closeDialog = () => {
    if (!activeDialog) return;

    window.clearTimeout(dialogErrorTimer);
    dialogErrorTimer = null;
    const dialog = activeDialog;
    const picker = currentPicker(dialog.dataset.nuoFormId);
    const state = picker ? getState(picker) : null;
    if (picker && !state.popupCommitted && !state.popupSubmitPending) resetPopupChoices(picker);
    if (state) {
      state.popupCommitted = false;
      state.popupSubmitPending = false;
    }
    dialog.nuoOverflowCleanup?.();
    activeDialog = null;
    dialog.remove();
  };

  const animateDialogClose = (dialog) => {
    if (!dialog?.open || typeof dialog.hide !== 'function') return;
    dialog.hide();
  };

  const buildDialog = (picker, submitter) => {
    closeDialog();

    const formId = pickerKey(picker);
    const dialog = document.createElement('x-modal');
    Object.defineProperty(dialog, 'clickOutsideDeactivates', {
      configurable: true,
      get: () => false
    });
    dialog.className = 'modal nuo-variant-choice-dialog color-scheme color-scheme--dialog';
    dialog.id = `nuo-variant-choice-dialog-${formId}`;
    dialog.dataset.nuoFormId = formId;
    if (config.alertBackground) {
      dialog.style.setProperty('--nuo-variant-alert-background', config.alertBackground);
    }
    if (config.alertTextColor) {
      dialog.style.setProperty('--nuo-variant-alert-text', config.alertTextColor);
    }
    dialog.setAttribute('initial-focus', 'false');
    dialog.setAttribute('tabindex', '-1');
    dialog.setAttribute('aria-labelledby', `nuo-variant-choice-title-${formId}`);
    dialog.innerHTML = `
      <span slot="header" class="nuo-variant-choice-dialog__title" id="nuo-variant-choice-title-${formId}"></span>
      <div class="nuo-variant-choice-dialog__surface">
        <p class="nuo-variant-choice-dialog__text"></p>
        <div class="nuo-variant-choice-dialog__body-shell">
          <div class="nuo-variant-choice-dialog__body" id="nuo-variant-choice-body-${formId}"></div>
          <button type="button" class="nuo-variant-choice-dialog__more" aria-controls="nuo-variant-choice-body-${formId}" aria-hidden="true" tabindex="-1"></button>
        </div>
        <div class="nuo-variant-choice-dialog__footer">
          <p class="nuo-variant-choice-dialog__error" role="alert" hidden></p>
          <button type="button" class="button button--solid w-full truncate-text nuo-variant-choice-dialog__action"></button>
        </div>
      </div>
    `;

    dialog.querySelector('.nuo-variant-choice-dialog__title').textContent = choiceText(
      picker,
      config.dialogTitle,
      config.dialogTitlePlural
    );
    dialog.querySelector('.nuo-variant-choice-dialog__text').textContent = choiceText(
      picker,
      config.dialogText,
      config.dialogTextPlural
    );
    dialog.querySelector('.nuo-variant-choice-dialog__error').textContent = choiceText(
      picker,
      config.errorLabel,
      config.errorLabelPlural
    );
    const body = dialog.querySelector('.nuo-variant-choice-dialog__body');
    const moreColors = dialog.querySelector('.nuo-variant-choice-dialog__more');
    moreColors.textContent = config.moreColorsLabel;
    dialog.querySelector('.nuo-variant-choice-dialog__action').dataset.nuoOriginalButtonText =
      submitter?.dataset.nuoOriginalButtonText || config.multipleConfirmLabel;
    document.body.append(dialog);
    activeDialog = dialog;

    let overflowFrame = 0;
    const syncOverflowHint = () => {
      window.cancelAnimationFrame(overflowFrame);
      overflowFrame = window.requestAnimationFrame(() => {
        if (!dialog.isConnected) return;

        const remaining = body.scrollHeight - body.scrollTop - body.clientHeight;
        const showMoreColors = Boolean(config.moreColorsLabel) && remaining > 2;
        moreColors.dataset.visible = String(showMoreColors);
        moreColors.setAttribute('aria-hidden', String(!showMoreColors));
        moreColors.tabIndex = showMoreColors ? 0 : -1;
      });
    };
    const overflowObserver = new ResizeObserver(syncOverflowHint);
    overflowObserver.observe(body);
    body.addEventListener('scroll', syncOverflowHint, { passive: true });
    moreColors.addEventListener('click', () => {
      body.scrollBy({
        top: Math.max(body.clientHeight * 0.7, 160),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });
    dialog.nuoOverflowCleanup = () => {
      window.cancelAnimationFrame(overflowFrame);
      overflowObserver.disconnect();
    };

    const render = () => {
      const livePicker = currentPicker(formId);
      if (!livePicker || !dialog.isConnected) return;

      const state = getState(livePicker);
      dialog.querySelector('.nuo-variant-choice-dialog__title').textContent = choiceText(
        livePicker,
        config.dialogTitle,
        config.dialogTitlePlural
      );
      dialog.querySelector('.nuo-variant-choice-dialog__text').textContent = choiceText(
        livePicker,
        config.dialogText,
        config.dialogTextPlural
      );
      dialog.querySelector('.nuo-variant-choice-dialog__error').textContent = choiceText(
        livePicker,
        config.errorLabel,
        config.errorLabelPlural
      );
      if (body.contains(document.activeElement)) dialog.focus({ preventScroll: true });
      body.replaceChildren();

      requiredFields(livePicker).forEach((field) => {
        const position = optionPosition(field);
        const group = document.createElement('section');
        group.className = 'nuo-variant-choice-dialog__group';

        const legend = document.createElement('p');
        legend.className = 'nuo-variant-choice-dialog__legend';
        const visibleLegends = Array.from(field.querySelectorAll('.variant-picker__option-info legend'))
          .map((item) => item.textContent.trim().replace(/:\s*$/, ''))
          .filter(Boolean);
        legend.textContent = naturalizeHeading(visibleLegends.join(' · ') || field.dataset.optionName || 'Nuanță');

        const options = document.createElement('div');
        options.className = 'nuo-variant-choice-dialog__options';

        field.querySelectorAll('input[data-option-position]').forEach((input) => {
          const originalLabel = document.querySelector(`label[for="${escapeSelector(input.id)}"]`);
          if (!originalLabel) return;

          const button = document.createElement('button');
          const chosen = state.confirmed.has(position) && input.checked;
          const swatchBackground = originalLabel.style.getPropertyValue('--swatch-background');
          button.type = 'button';
          button.className = 'nuo-variant-choice-dialog__option';
          button.dataset.optionPosition = position;
          button.dataset.optionValue = input.value;
          button.setAttribute('aria-pressed', chosen ? 'true' : 'false');
          button.disabled = state.variantUpdating
            || input.disabled
            || originalLabel.getAttribute('aria-disabled') === 'true';

          const swatch = document.createElement('span');
          swatch.className = 'nuo-variant-choice-dialog__swatch';
          if (swatchBackground) swatch.style.setProperty('--nuo-swatch', swatchBackground);

          const label = document.createElement('span');
          label.className = 'nuo-variant-choice-dialog__option-label';
          label.textContent = inputLabel(input);

          button.append(swatch, label);
          button.addEventListener('click', () => {
            const refreshedPicker = currentPicker(formId);
            const refreshedField = requiredFields(refreshedPicker).find((candidate) => optionPosition(candidate) === position);
            const refreshedInput = Array.from(refreshedField?.querySelectorAll('input[data-option-position]') || [])
              .find((candidate) => candidate.value === input.value);

            if (!refreshedInput) return;

            markChoice(refreshedPicker, refreshedField, refreshedInput);
            if (!refreshedInput.checked) {
              refreshedInput.checked = true;
              refreshedInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
            window.setTimeout(render, 0);
          });

          options.append(button);
        });

        group.append(legend, options);
        body.append(group);
      });
      syncOverflowHint();

      const complete = isComplete(livePicker, state);
      const labels = selectedLabels(livePicker, state);
      const action = dialog.querySelector('.nuo-variant-choice-dialog__action');
      action.disabled = state.variantUpdating;
      action.toggleAttribute('aria-busy', state.variantUpdating);
      if (state.variantUpdating) {
        setButtonText(action, originalButtonText(action));
      } else if (complete) {
        const content = confirmedButtonContent(labels);
        setButtonText(action, content.primary, content.secondary, content.multiple);
      } else {
        setButtonText(action, originalButtonText(action));
      }
      dialog.querySelector('.nuo-variant-choice-dialog__error').hidden = !state.attempted || complete;

      if (!state.variantUpdating && document.activeElement === dialog) {
        const chosenOptions = body.querySelectorAll('.nuo-variant-choice-dialog__option[aria-pressed="true"]');
        chosenOptions.item(chosenOptions.length - 1)?.focus({ preventScroll: true });
      }
    };

    dialog.addEventListener('dialog:after-hide', () => {
      window.clearTimeout(dialogErrorTimer);
      dialogErrorTimer = null;
      dialog.nuoOverflowCleanup?.();
      if (activeDialog === dialog) activeDialog = null;
      dialog.remove();
      if (submitter?.isConnected) submitter.focus({ preventScroll: true });
    });
    dialog.querySelector('.nuo-variant-choice-dialog__action').addEventListener('click', () => {
      const livePicker = currentPicker(formId);
      if (!livePicker) return;

      const state = getState(livePicker);
      const complete = isComplete(livePicker, state);

      if (!complete) {
        state.attempted = true;
        render();
        dialog.querySelector('.nuo-variant-choice-dialog__option:not([aria-pressed="true"])')?.focus();
        window.clearTimeout(dialogErrorTimer);
        dialogErrorTimer = window.setTimeout(() => {
          const currentState = states.get(formId);
          if (!currentState || !dialog.isConnected || !dialog.open) return;
          currentState.attempted = false;
          render();
        }, 4500);
        return;
      }

      if (state.variantUpdating) return;

      const form = document.getElementById(formId);
      const liveSubmitter = submitter?.isConnected
        ? submitter
        : formButtons(formId).find((button) => !button.disabled);

      state.bypassOnce = true;
      state.popupSubmitPending = true;
      dialog.addEventListener('dialog:after-hide', () => form?.requestSubmit(liveSubmitter), { once: true });
      animateDialogClose(dialog);
    });

    document.getElementById(formId)?.addEventListener('variant:change', () => window.setTimeout(render, 0), { signal: (() => {
      const controller = new AbortController();
      dialog.addEventListener('dialog:after-hide', () => controller.abort(), { once: true });
      return controller.signal;
    })() });

    render();
    dialog.show();
    syncOverflowHint();
  };

  const promptForChoice = (picker, submitter) => {
    const state = getState(picker);

    if (picker.getAttribute('context') === 'quick_buy') {
      state.attempted = true;
      updatePicker(picker);
      focusPendingChoice(picker);
      return;
    }

    state.attempted = false;
    updatePicker(picker);
    buildDialog(picker, submitter);
  };

  const initializePicker = (picker) => {
    if (picker.dataset.nuoExplicitInitialized === 'true' || requiredFields(picker).length === 0) return;
    const existingState = states.get(pickerKey(picker));
    if (existingState?.variantUpdating) existingState.variantUpdating = false;
    picker.dataset.nuoExplicitInitialized = 'true';
    getState(picker);
    attachVariantChangeListener(picker);
    updatePicker(picker);
  };

  const initializeAll = (root = document) => {
    if (root.matches?.('variant-picker')) initializePicker(root);
    root.querySelectorAll?.('variant-picker').forEach(initializePicker);
  };

  const inputFromGesture = (event) => {
    const directInput = event.target.closest?.('input[type="radio"]');
    if (directInput) return directInput;
    return event.target.closest?.('label[for]')?.control || null;
  };

  const registerExplicitGesture = (event) => {
    if (!event.isTrusted) return;
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;

    const kachingOption = event.target.closest?.('.kaching-bundles__swatch-dropdown__option[role="option"]');
    if (kachingOption && (event.type === 'pointerdown' || event.type === 'keydown')) {
      confirmKachingOption(kachingOption);
    }

    const input = inputFromGesture(event);
    if (!input) return;

    const field = input.closest('.variant-picker__option[data-nuo-explicit-choice]');
    const picker = field?.closest('variant-picker');
    if (picker) {
      // An unchecked radio is confirmed from its native `change` event below.
      // Updating the CTA during pointerdown can move the label before click's
      // default action checks the radio, especially inside Quick Buy.
      if (event.type === 'click' && input.checked) {
        markChoice(picker, field, input);
      } else if (!input.checked) {
        userChoiceInputs.add(input);
        if (event.type === 'click') window.setTimeout(() => userChoiceInputs.delete(input), 0);
      }
    }

    const cardInput = input.matches('product-card .product-card__info input[type="radio"][data-variant-id]') ? input : null;
    if (cardInput) {
      const card = cardInput.closest('product-card');
      const modal = card?.querySelector('quick-buy-modal');

      if (modal) {
        const handle = modal.getAttribute('handle').replace(/^\/?products\//, '');
        const handleUrl = new URL(`/products/${handle}`, window.location.origin);
        handleUrl.searchParams.set('variant', cardInput.dataset.variantId);
        modal.setAttribute('handle', `${handleUrl.pathname.replace(/^\/products\//, '')}${handleUrl.search}`);
        modal.dataset.nuoExplicitCardVariant = cardInput.dataset.variantId;
        modal.dataset.nuoExplicitCardLabel = inputLabel(cardInput);
        modal.dataset.nuoExplicitCardOptionPosition = cardInput.dataset.optionPosition || '';
      }
    }
  };

  document.addEventListener('pointerdown', registerExplicitGesture, true);
  document.addEventListener('keydown', registerExplicitGesture, true);
  document.addEventListener('click', registerExplicitGesture, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('input[type="radio"][data-option-position]');
    if (!input || !userChoiceInputs.has(input)) return;
    userChoiceInputs.delete(input);

    const field = input?.closest('.variant-picker__option[data-nuo-explicit-choice]');
    const picker = field?.closest('variant-picker');
    if (picker && input.checked) markChoice(picker, field, input, true);
  }, true);

  document.addEventListener('dialog:after-show', (event) => {
    const popup = event.target;
    if (!(popup instanceof Element) || !popup.matches('quick-buy-modal, .nuo-variant-choice-dialog')) return;

    const formId = popup.matches('.nuo-variant-choice-dialog')
      ? popup.dataset.nuoFormId
      : popup.querySelector('variant-picker')?.getAttribute('form-id');
    const picker = typeof formId === 'string' ? currentPicker(formId) : null;
    if (!picker) return;

    const state = getState(picker);
    state.popupCommitted = false;
    state.popupSubmitPending = false;
  }, true);

  document.addEventListener('dialog:after-hide', (event) => {
    const popup = event.target;
    if (!(popup instanceof Element) || !popup.matches('quick-buy-modal, .nuo-variant-choice-dialog')) return;

    const formId = popup.matches('.nuo-variant-choice-dialog')
      ? popup.dataset.nuoFormId
      : popup.querySelector('variant-picker')?.getAttribute('form-id');
    const picker = typeof formId === 'string' ? currentPicker(formId) : null;
    if (!picker) return;

    const state = getState(picker);
    if (!state.popupCommitted && !state.popupSubmitPending) resetPopupChoices(picker);
    state.popupCommitted = false;
    state.popupSubmitPending = false;
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !(form.getAttribute('action') || '').includes('/cart/add')) return;

    const formId = form.getAttribute('id');
    const picker = currentPicker(formId);
    if (!picker || requiredFields(picker).length === 0) return;

    if (!kachingIsComplete(picker)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showKachingStatus(picker);
      focusPendingKachingChoice(picker);
      return;
    }

    const state = getState(picker);
    if (state.variantUpdating) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (state.bypassOnce) {
      state.bypassOnce = false;
      lastAddedSelection = {
        formId,
        modalId: picker.closest('quick-buy-modal')?.id || null
      };
      return;
    }

    if (isComplete(picker, state)) {
      lastAddedSelection = {
        formId,
        modalId: picker.closest('quick-buy-modal')?.id || null
      };
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    promptForChoice(picker, event.submitter);
  }, true);

  document.addEventListener('variant:change', (event) => {
    const formId = event.target instanceof HTMLFormElement
      ? event.target.getAttribute('id')
      : event.detail?.formId;
    const picker = typeof formId === 'string' ? currentPicker(formId) : null;
    if (picker) {
      getState(picker).variantUpdating = false;
      window.setTimeout(() => updatePicker(picker), 0);
    }
  });

  const showToast = (event) => {
    if (lastAddedSelection) {
      const picker = currentPicker(lastAddedSelection.formId);
      if (picker) getState(picker).popupCommitted = true;
    }

    if (!config.toastEnabled || !window.matchMedia('(max-width: 699px)').matches || !lastAddedSelection) return;

    const item = event.detail?.items?.[0];
    const variant = item?.variant_title;
    if (!item || !variant || variant === 'Default Title') return;

    let toast = document.querySelector('.nuo-variant-choice-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'nuo-variant-choice-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML = '<span class="nuo-variant-choice-toast__message"></span><button type="button" class="nuo-variant-choice-toast__change">Schimbă</button>';
      document.body.append(toast);
    }

    toast.querySelector('.nuo-variant-choice-toast__message').textContent = mergeText(config.toastLabel, {
      product: item.product_title || item.title || '',
      variant
    });

    toast.querySelector('.nuo-variant-choice-toast__change').onclick = () => {
      toast.removeAttribute('open');
      const modalTrigger = lastAddedSelection.modalId
        ? document.querySelector(`[aria-controls="${escapeSelector(lastAddedSelection.modalId)}"]`)
        : null;

      if (modalTrigger) {
        modalTrigger.click();
      } else {
        const picker = currentPicker(lastAddedSelection.formId);
        if (picker) buildDialog(picker, formButtons(lastAddedSelection.formId)[0]);
      }
    };

    window.clearTimeout(toastTimer);
    toast.setAttribute('open', '');
    toastTimer = window.setTimeout(() => toast.removeAttribute('open'), 6500);
  };

  document.addEventListener('variant:add', showToast);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) initializeAll(node);
    }));
  });

  initializeAll();
  updateKachingSelectors();
  observer.observe(document.documentElement, { childList: true, subtree: true });
  const kachingObserver = new MutationObserver(scheduleKachingRefresh);
  kachingObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
